// useJourneyTopics.ts
// ====================
// Custom hook for managing topics within a specific journey
// Feature #85 - React hook for topic CRUD operations with Firestore

import { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  createTopic,
  createTopicsBatch,
  getTopicsByJourney,
  updateTopic,
  deleteTopic,
  updateTopicSubtopics,
  markSubtopicCompleted,
} from '../services/firebase';
import i18n from '../i18n';
import type { Topic, Subtopic } from '../types';

// Helper to convert Firebase Timestamp to Date/string
function normalizeTopicDates(topic: Record<string, unknown>): Topic {
  const normalized = { ...topic } as unknown as Topic;

  // Handle createdAt conversion
  if (topic.createdAt) {
    if (typeof topic.createdAt === 'object' && 'toDate' in (topic.createdAt as object)) {
      normalized.createdAt = (topic.createdAt as { toDate: () => Date }).toDate().toISOString();
    } else if (topic.createdAt instanceof Date) {
      normalized.createdAt = topic.createdAt.toISOString();
    }
  }

  return normalized;
}

export interface UseJourneyTopicsResult {
  // Data
  topics: Topic[];
  loading: boolean;
  error: string | null;

  // Actions
  fetchTopics: () => Promise<void>;
  addTopic: (topicData: Omit<Topic, 'id'>) => Promise<{ success: boolean; id?: string; error?: string }>;
  addTopicsBatch: (topics: Omit<Topic, 'id'>[]) => Promise<{ success: boolean; ids?: string[]; error?: string }>;
  editTopic: (topicId: string, data: Partial<Omit<Topic, 'id'>>) => Promise<{ success: boolean; error?: string }>;
  removeTopic: (topicId: string) => Promise<{ success: boolean; error?: string }>;
  deleteAllTopics: () => Promise<{ success: boolean; error?: string }>;
  reorderTopics: (topicIds: string[]) => Promise<{ success: boolean; error?: string }>;
  setSubtopics: (topicId: string, subtopics: Subtopic[]) => Promise<{ success: boolean; error?: string }>;
  toggleSubtopicComplete: (topicId: string, subtopicId: string, completed: boolean) => Promise<{ success: boolean; error?: string }>;
  clearTopics: () => void;
}

export function useJourneyTopics(journeyId: string | undefined): UseJourneyTopicsResult {
  const { user, isMockAuth } = useAuth();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // LocalStorage key for mock auth fallback
  const getLocalStorageKey = () =>
    journeyId && user?.uid ? `lyra_topics_${user.uid}_${journeyId}` : null;

  // Fetch all topics for the journey
  const fetchTopics = useCallback(async () => {
    if (!journeyId || !user?.uid) {
      setTopics([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isMockAuth) {
        // Use localStorage for mock auth (development only)
        console.log('[useJourneyTopics] Using localStorage (mock auth)');
        const localKey = getLocalStorageKey();
        if (localKey) {
          const saved = localStorage.getItem(localKey);
          const parsed = saved ? JSON.parse(saved) : [];
          setTopics(parsed);
        }
      } else {
        // Use Firebase (production)
        console.log('[useJourneyTopics] Fetching from Firebase for journey:', journeyId);
        const result = await getTopicsByJourney(journeyId);
        if (result.success && result.topics) {
          const normalized = result.topics.map((t) =>
            normalizeTopicDates(t as unknown as Record<string, unknown>)
          );
          console.log(`[useJourneyTopics] Loaded ${normalized.length} topics from Firebase`);
          setTopics(normalized);
        } else {
          setError(result.error || 'Kunne ikke hente emner');
          setTopics([]);
        }
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error fetching topics:', err);
      setError(i18n.t('errors:fetch.topics'));
      setTopics([]);
    } finally {
      setLoading(false);
    }
  }, [journeyId, user?.uid, isMockAuth]);

  // Auto-fetch when journeyId changes
  useEffect(() => {
    if (journeyId) {
      fetchTopics();
    }
  }, [journeyId, fetchTopics]);

  // Add a single topic
  const addTopic = useCallback(async (
    topicData: Omit<Topic, 'id'>
  ): Promise<{ success: boolean; id?: string; error?: string }> => {
    if (!journeyId || !user?.uid) {
      return { success: false, error: 'Ingen bruger eller forløb valgt' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: use localStorage
        const newTopic: Topic = {
          id: `topic_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...topicData,
          learningJourneyId: journeyId,
          userId: user.uid,
          order: topics.length,
          createdAt: new Date().toISOString(),
        };

        const localKey = getLocalStorageKey();
        if (localKey) {
          const existing = [...topics, newTopic];
          localStorage.setItem(localKey, JSON.stringify(existing));
          setTopics(existing);
        }

        return { success: true, id: newTopic.id };
      } else {
        // Firebase
        const result = await createTopic(journeyId, {
          ...topicData,
          userId: user.uid,
          order: topics.length,
        });

        if (result.success) {
          await fetchTopics();
        }

        return result;
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error adding topic:', err);
      return { success: false, error: 'Der opstod en fejl ved oprettelse af emne' };
    }
  }, [journeyId, user?.uid, isMockAuth, topics, fetchTopics]);

  // Add multiple topics at once
  const addTopicsBatch = useCallback(async (
    topicsData: Omit<Topic, 'id'>[]
  ): Promise<{ success: boolean; ids?: string[]; error?: string }> => {
    if (!journeyId || !user?.uid) {
      return { success: false, error: 'Ingen bruger eller forløb valgt' };
    }

    try {
      if (isMockAuth) {
        // Mock auth: use localStorage
        const newTopics: Topic[] = topicsData.map((t, idx) => ({
          id: `topic_${Date.now()}_${idx}_${Math.random().toString(36).substr(2, 9)}`,
          ...t,
          learningJourneyId: journeyId,
          userId: user.uid,
          order: topics.length + idx,
          createdAt: new Date().toISOString(),
        }));

        const localKey = getLocalStorageKey();
        if (localKey) {
          const existing = [...topics, ...newTopics];
          localStorage.setItem(localKey, JSON.stringify(existing));
          setTopics(existing);
        }

        return { success: true, ids: newTopics.map(t => t.id) };
      } else {
        // Firebase
        const result = await createTopicsBatch(
          journeyId,
          topicsData.map((t, idx) => ({
            ...t,
            userId: user.uid,
            order: topics.length + idx,
          }))
        );

        if (result.success) {
          await fetchTopics();
        }

        return result;
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error adding topics batch:', err);
      return { success: false, error: 'Der opstod en fejl ved oprettelse af emner' };
    }
  }, [journeyId, user?.uid, isMockAuth, topics, fetchTopics]);

  // Edit a topic
  const editTopic = useCallback(async (
    topicId: string,
    data: Partial<Omit<Topic, 'id'>>
  ): Promise<{ success: boolean; error?: string }> => {
    if (!journeyId || !user?.uid) {
      return { success: false, error: 'Ingen bruger eller forløb valgt' };
    }

    try {
      if (isMockAuth) {
        // Mock auth
        const localKey = getLocalStorageKey();
        if (localKey) {
          const updated = topics.map((t) =>
            t.id === topicId ? { ...t, ...data } : t
          );
          localStorage.setItem(localKey, JSON.stringify(updated));
          setTopics(updated);
        }
        return { success: true };
      } else {
        // Firebase
        const result = await updateTopic(journeyId, topicId, data);
        if (result.success) {
          setTopics((prev) =>
            prev.map((t) => (t.id === topicId ? { ...t, ...data } : t))
          );
        }
        return result;
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error editing topic:', err);
      return { success: false, error: 'Der opstod en fejl ved opdatering af emne' };
    }
  }, [journeyId, user?.uid, isMockAuth, topics]);

  // Remove a topic
  const removeTopic = useCallback(async (
    topicId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!journeyId || !user?.uid) {
      return { success: false, error: 'Ingen bruger eller forløb valgt' };
    }

    try {
      if (isMockAuth) {
        // Mock auth
        const localKey = getLocalStorageKey();
        if (localKey) {
          const filtered = topics.filter((t) => t.id !== topicId);
          localStorage.setItem(localKey, JSON.stringify(filtered));
          setTopics(filtered);
        }
        return { success: true };
      } else {
        // Firebase
        const result = await deleteTopic(journeyId, topicId);
        if (result.success) {
          setTopics((prev) => prev.filter((t) => t.id !== topicId));
        }
        return result;
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error removing topic:', err);
      return { success: false, error: 'Der opstod en fejl ved sletning af emne' };
    }
  }, [journeyId, user?.uid, isMockAuth, topics]);

  // Delete all topics (used when replacing entire plan) - Feature #102
  const deleteAllTopics = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!journeyId || !user?.uid) {
      return { success: false, error: 'Ingen bruger eller forløb valgt' };
    }

    if (topics.length === 0) {
      return { success: true }; // Nothing to delete
    }

    try {
      console.log(`[useJourneyTopics] Deleting all ${topics.length} topics for journey ${journeyId}`);

      if (isMockAuth) {
        // Mock auth: clear localStorage
        const localKey = getLocalStorageKey();
        if (localKey) {
          localStorage.setItem(localKey, JSON.stringify([]));
          setTopics([]);
        }
        return { success: true };
      } else {
        // Firebase: delete each topic
        const deletePromises = topics.map((t) => deleteTopic(journeyId, t.id));
        const results = await Promise.all(deletePromises);

        // Check if any failed
        const failures = results.filter((r) => !r.success);
        if (failures.length > 0) {
          console.warn(`[useJourneyTopics] ${failures.length} topics failed to delete`);
        }

        // Clear local state
        setTopics([]);

        return failures.length === 0
          ? { success: true }
          : { success: true, error: `${failures.length} af ${topics.length} emner kunne ikke slettes` };
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error deleting all topics:', err);
      return { success: false, error: 'Der opstod en fejl ved sletning af emner' };
    }
  }, [journeyId, user?.uid, isMockAuth, topics]);

  // Reorder topics
  const reorderTopics = useCallback(async (
    topicIds: string[]
  ): Promise<{ success: boolean; error?: string }> => {
    if (!journeyId || !user?.uid) {
      return { success: false, error: 'Ingen bruger eller forløb valgt' };
    }

    try {
      // Create new ordered list
      const reordered: Topic[] = [];
      for (let idx = 0; idx < topicIds.length; idx++) {
        const topic = topics.find((t) => t.id === topicIds[idx]);
        if (topic) {
          reordered.push({ ...topic, order: idx });
        }
      }

      // Update local state immediately
      setTopics(reordered);

      if (isMockAuth) {
        // Mock auth
        const localKey = getLocalStorageKey();
        if (localKey) {
          localStorage.setItem(localKey, JSON.stringify(reordered));
        }
        return { success: true };
      } else {
        // Firebase: update each topic's order
        for (let i = 0; i < reordered.length; i++) {
          await updateTopic(journeyId, reordered[i].id, { order: i });
        }
        return { success: true };
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error reordering topics:', err);
      return { success: false, error: 'Der opstod en fejl ved omarrangering af emner' };
    }
  }, [journeyId, user?.uid, isMockAuth, topics]);

  // Set subtopics for a topic
  const setSubtopics = useCallback(async (
    topicId: string,
    subtopics: Subtopic[]
  ): Promise<{ success: boolean; error?: string }> => {
    if (!journeyId || !user?.uid) {
      return { success: false, error: 'Ingen bruger eller forløb valgt' };
    }

    try {
      if (isMockAuth) {
        // Mock auth
        const localKey = getLocalStorageKey();
        if (localKey) {
          const updated = topics.map((t) =>
            t.id === topicId ? { ...t, subtopics } : t
          );
          localStorage.setItem(localKey, JSON.stringify(updated));
          setTopics(updated);
        }
        return { success: true };
      } else {
        // Firebase
        const result = await updateTopicSubtopics(journeyId, topicId, subtopics);
        if (result.success) {
          setTopics((prev) =>
            prev.map((t) => (t.id === topicId ? { ...t, subtopics } : t))
          );
        }
        return result;
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error setting subtopics:', err);
      return { success: false, error: 'Der opstod en fejl ved opdatering af underemner' };
    }
  }, [journeyId, user?.uid, isMockAuth, topics]);

  // Toggle subtopic completion
  const toggleSubtopicComplete = useCallback(async (
    topicId: string,
    subtopicId: string,
    completed: boolean
  ): Promise<{ success: boolean; error?: string }> => {
    if (!journeyId || !user?.uid) {
      return { success: false, error: 'Ingen bruger eller forløb valgt' };
    }

    try {
      if (isMockAuth) {
        // Mock auth
        const localKey = getLocalStorageKey();
        if (localKey) {
          const updated = topics.map((t) => {
            if (t.id === topicId && t.subtopics) {
              return {
                ...t,
                subtopics: t.subtopics.map((s) =>
                  s.id === subtopicId ? { ...s, isCompleted: completed, completed } : s
                ),
              };
            }
            return t;
          });
          localStorage.setItem(localKey, JSON.stringify(updated));
          setTopics(updated);
        }
        return { success: true };
      } else {
        // Firebase
        const result = await markSubtopicCompleted(journeyId, topicId, subtopicId, completed);
        if (result.success) {
          setTopics((prev) =>
            prev.map((t) => {
              if (t.id === topicId && t.subtopics) {
                return {
                  ...t,
                  subtopics: t.subtopics.map((s) =>
                    s.id === subtopicId ? { ...s, isCompleted: completed, completed } : s
                  ),
                };
              }
              return t;
            })
          );
        }
        return result;
      }
    } catch (err) {
      console.error('[useJourneyTopics] Error toggling subtopic:', err);
      return { success: false, error: 'Der opstod en fejl' };
    }
  }, [journeyId, user?.uid, isMockAuth, topics]);

  // Clear topics (when switching journeys)
  const clearTopics = useCallback(() => {
    setTopics([]);
    setError(null);
  }, []);

  return {
    topics,
    loading,
    error,
    fetchTopics,
    addTopic,
    addTopicsBatch,
    editTopic,
    removeTopic,
    deleteAllTopics,
    reorderTopics,
    setSubtopics,
    toggleSubtopicComplete,
    clearTopics,
  };
}

export default useJourneyTopics;
